import {
	EnvironmentEmailSettingsModel,
	EnvironmentEmailSettingsSaveModel,
	ManagedSignatureModel,
	getViewModel,
	save
} from "client/Api/Email/EnvironmentSettings/environmentEmailSettings";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { EnvironmentEmailForm } from "./EnvironmentEmailForm";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration
} from "client/Validation";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useHasRequiredLicenseForFeature,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPage } from "../../SettingsPage";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { isNotNil } from "client/Misc/utils";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import { SignatureConstraints } from "client/Api/Email/Signatures/Personal/personalSignatureSettings";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { EnvironmentFeature } from "client/Features/FeaturePackages/misc";
import { ToolbarAction } from "client/Components";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const EnvironmentEmailPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [signatureConstraints, setSignatureConstraints] = useState<SignatureConstraints | undefined>(undefined);

	const [resetFormData, setResetFormData] = useState<EnvironmentEmailSettingsSaveModel>(defaultSaveModel);
	const [formData, setFormData] = useState<EnvironmentEmailSettingsSaveModel>(defaultSaveModel);

	const isManagedSignatureAvailable: boolean = useHasRequiredLicenseForFeature(EnvironmentFeature.ManagedSignature);

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<EnvironmentEmailSettingsSaveModel> => {
			let result: IValidationConfiguration<EnvironmentEmailSettingsSaveModel> = staticValidationConfiguration;

			result = result.clone();

			if (!isManagedSignatureAvailable) {
				return result;
			}

			const managedSignatureValidation: IObjectValidatorBuilder<ManagedSignatureModel> = result
				.when(
					x => x.isManagedSignatureEnabled === true,
				)
				.forObject("managedSignature")
				.whenType(isNotNil);

			managedSignatureValidation
				.forScalar("name")
				.add(
					isNotNullNorUndefined(),
					isNotEmpty()
				);

			managedSignatureValidation
				.forScalar("content")
				.add(
					isNotNullNorUndefined(),
					isNotEmpty()
				);

			return result;
		},
		[isManagedSignatureAvailable]
	);

	const formHandler: IFormHandler<EnvironmentEmailSettingsSaveModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	useActiveSettingsMenuItem(SettingsPageKind.EnvironmentEmail);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const viewModel: EnvironmentEmailSettingsModel = await getViewModel(signal)
						.catch(handlePageDataLoadingError);

					setSignatureConstraints(viewModel.signatureConstraints);
					setFormData(convertToSaveModel(viewModel, isManagedSignatureAvailable));
					setResetFormData(convertToSaveModel(viewModel, isManagedSignatureAvailable));

					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, isManagedSignatureAvailable]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			const validation: IRootValidation<EnvironmentEmailSettingsSaveModel> = formHandler.validation;

			const validationResult: InputValidationResult = await validation
				.validate(globalCancellation);

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			try {
				setIsBusy(true);

				const model: EnvironmentEmailSettingsSaveModel = prepareForSaving(
					formData,
					isManagedSignatureAvailable
				);

				await save(model, globalCancellation);

				alertManager.showSuccess(localize("_ChangesSaved"));
				setResetFormData(formData);
				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);

				if (!validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_ChangesNotSaved"));
					console.error(e);
				}
			}
		},
		[
			alertManager,
			formData,
			globalCancellation,
			formHandler.validation,
			isManagedSignatureAvailable
		]
	);

	const resetHandler = useCallback(
		(): void => {
			setFormData(resetFormData);

			const validation: IRootValidation<EnvironmentEmailSettingsSaveModel> = formHandler.validation;
			validation.clearServerErrors();
			validation.validate();
		},
		[
			resetFormData,
			formHandler.validation
		]
	);

	const defaultActions: readonly EnvironmentEmailAction[] = useMemo(
		() => {
			return listDefaultActions({
				doneHandler: () => saveHandler(),
				cancelHandler: () => resetHandler()
			});
		},
		[
			resetHandler,
			saveHandler
		]
	);

	const topBarActions = useMemo(
		(): readonly EnvironmentEmailAction[] => {
			return buildActionsForTopBar<undefined, EnvironmentEmailAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={undefined}
			title={localize("_Email")}
		>
			<EnvironmentEmailForm
				attachable={formHandler}
				signatureConstraints={signatureConstraints}
				isManagedSignatureAvailable={isManagedSignatureAvailable}
			/>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
EnvironmentEmailPage.displayName = "EnvironmentEmailPage";

const staticValidationConfiguration: IValidationConfiguration<EnvironmentEmailSettingsSaveModel> =
	createValidationConfiguration<EnvironmentEmailSettingsSaveModel>();

type EnvironmentEmailAction = ToolbarAction<undefined>;

type DefaultActionsHandlers = {
	doneHandler(): void;
	cancelHandler(): void;
};

function listDefaultActions(
	handlers: DefaultActionsHandlers
): readonly EnvironmentEmailAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => handlers.doneHandler(),
			icon: "floppy-disk",
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			canExecute: () => true,
			execute: () => handlers.cancelHandler(),
			icon: "xmark"
		}
	];
}

function convertToSaveModel(
	model: EnvironmentEmailSettingsModel,
	isManagedSignatureAvailable: boolean
): EnvironmentEmailSettingsSaveModel {

	const {
		automaticallyShowSenderImages,
		defaultComposerFontStyle,
		isManagedSignatureEnabled,
		managedSignature
	} = model;

	return isManagedSignatureAvailable
		? {
			automaticallyShowSenderImages,
			defaultComposerFontStyle,
			isManagedSignatureEnabled,
			managedSignature: isManagedSignatureEnabled
				? managedSignature
				: {
					content: "",
					name: "",
					shouldBeInsertedAutomatically: false
				}
		} : {
			automaticallyShowSenderImages,
			defaultComposerFontStyle,
			isManagedSignatureEnabled: false
		};
}

function prepareForSaving(
	model: EnvironmentEmailSettingsSaveModel,
	isManagedSignatureAvailable: boolean
): EnvironmentEmailSettingsSaveModel {
	const {
		automaticallyShowSenderImages,
		defaultComposerFontStyle
	} = model;

	return model.isManagedSignatureEnabled && isManagedSignatureAvailable
		? {
			automaticallyShowSenderImages,
			defaultComposerFontStyle,
			isManagedSignatureEnabled: true,
			managedSignature: model.managedSignature
		}
		: {
			automaticallyShowSenderImages,
			defaultComposerFontStyle,
			isManagedSignatureEnabled: false
		};
}

const defaultSaveModel: EnvironmentEmailSettingsSaveModel = {
	automaticallyShowSenderImages: false,
	defaultComposerFontStyle: {
		bold: false,
		italic: false,
		underline: false,
		family: null,
		size: null,
		color: null
	},
	isManagedSignatureEnabled: false,
	managedSignature: undefined
};

export const environmentEmailSettingsRouting = {
	page: "/EnvironmentEmailSettings"
};